/* global MutationObserver */

import { generateRandomID, isObject, isSet, isString } from '../helpers/utils.js'

/**
 * Represents a Theme instance.
 */
class Theme {
  constructor (icons = null) {
    this.icons = icons
    this.useToggleEvents = true
    this.btnContents = true
    this.btnIcons = true
    this.init()
  }

  /**
   * Inits some instance properties
   */
  init () {
    this.useToggleEvents = true
  }

  /**
   * Used to wrap the editor UI elements
   */
  getEditorContainer () {
    const html = document.createElement('div')
    html.classList.add('jedi-editor-container')
    return html
  }

  /**
   * Used to group several controls
   */
  getFieldset () {
    const html = document.createElement('fieldset')
    html.classList.add('jedi-editor-fieldset')
    html.setAttribute('role', 'group')
    return html
  }

  /**
   * Represents a caption for the content of its parent fieldset
   */
  getLegend (config) {
    const left = document.createElement('div')
    const right = document.createElement('div')
    const legend = document.createElement('legend')
    const legendText = document.createElement('label')
    const icon = document.createElement('i')
    const infoContainer = document.createElement('span')
    const dummyInput = document.createElement('input')
    const legendLabelId = 'legend-label-' + config.id
    const dummyInputId = 'legend-dummy-input-' + config.id

    left.classList.add('jedi-editor-legend-left')
    right.classList.add('jedi-editor-legend-right')
    right.style.display = 'flex'
    right.style.alignItems = 'center'

    legend.classList.add('jedi-editor-legend')
    legend.style.fontSize = 'inherit'
    legend.setAttribute('aria-labelledby', legendLabelId)

    legendText.classList.add('jedi-title')
    legendText.classList.add('jedi-legend')
    legendText.setAttribute('id', legendLabelId)
    legendText.innerHTML = config.content

    if (config.titleIconClass) {
      this.addIconClass(icon, config.titleIconClass)
      icon.style.marginRight = '4px'
    }

    legendText.style.marginRight = '4px'

    infoContainer.classList.add('jedi-editor-info-container')
    infoContainer.setAttribute('for', dummyInputId)

    dummyInput.setAttribute('aria-hidden', 'true')
    dummyInput.setAttribute('type', 'hidden')
    dummyInput.setAttribute('id', dummyInputId)

    this.visuallyHidden(dummyInput)

    if (config.titleHidden) {
      this.visuallyHidden(legendText)
    }

    legend.appendChild(left)
    legend.appendChild(right)
    if (config.titleIconClass) {
      left.appendChild(icon)
    }
    left.appendChild(legendText)
    left.appendChild(infoContainer)
    legendText.appendChild(dummyInput)

    return { left, right, legend, legendText, infoContainer }
  }

  /**
   * Used to group several controls
   */
  getRadioFieldset () {
    const fieldset = document.createElement('fieldset')
    fieldset.classList.add('jedi-editor-radio-fieldset')
    fieldset.setAttribute('role', 'group')
    fieldset.style.marginBottom = '15px'
    fieldset.style.fontSize = 'inherit'
    return fieldset
  }

  /**
   * Represents a caption for the content of its parent fieldset
   */
  getRadioLegend (config) {
    const legendLabelId = 'legend-label-' + config.id
    const legend = document.createElement('legend')
    const legendText = document.createElement('label')
    const icon = document.createElement('i')
    const dummyInput = document.createElement('input')

    legend.classList.add('jedi-editor-legend')
    legend.style.fontSize = 'inherit'
    legend.setAttribute('aria-labelledby', legendLabelId)

    legendText.classList.add('jedi-title')
    legendText.classList.add('jedi-label')
    legendText.innerHTML = config.content
    legendText.setAttribute('id', legendLabelId)
    legendText.style.marginRight = '4px'

    dummyInput.setAttribute('aria-hidden', 'true')
    dummyInput.setAttribute('type', 'hidden')
    dummyInput.setAttribute('disabled', '')

    this.visuallyHidden(dummyInput)

    if (config.titleIconClass) {
      this.addIconClass(icon, config.titleIconClass)
      icon.style.marginRight = '4px'
      legend.appendChild(icon)
    }

    legend.appendChild(legendText)
    legendText.appendChild(dummyInput)

    return { legend, legendText, icon }
  }

  /**
   * Represents a caption for the content of its parent fieldset
   */
  getLabel (config) {
    const label = document.createElement('label')
    const labelText = document.createElement('span')
    const icon = document.createElement('i')

    label.setAttribute('for', config.for)
    label.classList.add('jedi-title')
    label.classList.add('jedi-label')
    labelText.innerHTML = config.text

    if (config.visuallyHidden) {
      this.visuallyHidden(label)
    }

    if (config.titleIconClass) {
      this.addIconClass(icon, config.titleIconClass)
      icon.style.marginRight = '4px'
    }

    if (config.titleIconClass) {
      label.appendChild(icon)
    }

    labelText.style.marginRight = '4px'
    label.appendChild(labelText)

    return { label, labelText, icon }
  }

  getFakeLabel (config) {
    const label = document.createElement('label')
    const labelText = document.createElement('span')
    const icon = document.createElement('i')
    const dummyInput = document.createElement('input')

    label.setAttribute('for', config.for)
    label.classList.add('jedi-title')
    label.classList.add('jedi-label')

    if (config.visuallyHidden) {
      this.visuallyHidden(label)
    }

    labelText.innerHTML = config.content

    if (config.titleIconClass) {
      this.addIconClass(icon, config.titleIconClass)
      icon.style.marginRight = '4px'
    }

    dummyInput.setAttribute('aria-hidden', 'true')
    dummyInput.setAttribute('type', 'hidden')
    dummyInput.setAttribute('disabled', '')
    dummyInput.setAttribute('id', config.for)
    this.visuallyHidden(dummyInput)

    if (config.titleIconClass) {
      label.appendChild(icon)
    }
    labelText.style.marginRight = '4px'
    label.appendChild(labelText)
    label.appendChild(dummyInput)

    return { label, labelText, icon, dummyInput }
  }

  /**
   * Returns a icon element
   */
  addIconClass (element, classes = '') {
    let iconClasses = classes.split(' ')
    iconClasses = iconClasses.filter((className) => className.length > 0)

    if (iconClasses) {
      iconClasses.forEach((className) => {
        element.classList.add(className)
      })
    }
  }

  getOptInWrapper () {
    const optInWrapper = document.createElement('span')
    const optInContainer = document.createElement('span')
    const otherContainer = document.createElement('span')

    optInWrapper.classList.add('jedi-opt-in-wrapper')
    optInWrapper.style.display = 'flex'
    optInWrapper.style.alignItems = 'center'

    optInContainer.classList.add('jedi-opt-in-container')

    otherContainer.classList.add('jedi-title-container')
    otherContainer.style.flexGrow = '1'

    optInWrapper.appendChild(otherContainer)
    optInWrapper.appendChild(optInContainer)

    return { optInWrapper, optInContainer, otherContainer }
  }

  /**
   * Container for complex editors like arrays and objects
   */
  getCard () {
    const html = document.createElement('div')
    html.classList.add('jedi-editor-card')
    return html
  }

  /**
   * Header for cards
   */
  getCardHeader () {
    const html = document.createElement('div')
    html.classList.add('jedi-editor-card-header')
    return html
  }

  /**
   * A body for the cards
   */
  getCardBody () {
    const html = document.createElement('div')
    html.classList.add('jedi-editor-card-body')
    return html
  }

  /**
   * A footer for array cards
   */
  getArrayFooter () {
    const html = document.createElement('div')
    html.classList.add('jedi-array-footer')
    html.style.display = 'flex'
    html.style.alignItems = 'center'
    return html
  }

  /**
   * Wrapper for editor actions buttons
   */
  getActionsSlot () {
    const html = document.createElement('div')
    html.classList.add('jedi-actions-slot')
    return html
  }

  /**
   * Wrapper for editor array specific actions buttons
   */
  getArrayActionsSlot () {
    const html = document.createElement('span')
    html.classList.add('jedi-array-actions-slot')
    return html
  }

  /**
   * Wrapper for child editors
   */
  getChildrenSlot () {
    const html = document.createElement('div')
    html.classList.add('jedi-children-slot')
    return html
  }

  /**
   * Wrapper used by EditorMultiple to embed an inline switcher next to a control's title
   */
  getSwitcherSlot () {
    const html = document.createElement('span')
    html.classList.add('jedi-switcher-slot')
    return html
  }

  /**
   * Per-owner wrapper inside a switcherSlot — lets more than one EditorMultiple
   * embed a switcher into the same slot (e.g. nested anyOf/oneOf) without one
   * overwriting the other.
   */
  getSwitcherOwner () {
    const html = document.createElement('span')
    html.classList.add('jedi-switcher-owner')
    html.style.marginInlineStart = '0.25rem'
    return html
  }

  /**
   * Wrapper for error messages
   */
  getMessagesSlot (config = {}) {
    const html = document.createElement('div')
    html.classList.add('jedi-messages-slot')
    html.setAttribute('aria-atomic', 'false')
    html.setAttribute('aria-live', 'polite')

    if (config.id) {
      html.setAttribute('id', config.id)
    }

    return html
  }

  /**
   * Wrapper for editor controls
   */
  getControlSlot () {
    const html = document.createElement('div')
    html.classList.add('jedi-control-slot')
    return html
  }

  /**
   * Toggles the ObjectEditor properties wrapper visibility
   */
  getPropertiesToggle (config) {
    const toggle = this.getButton(config)
    toggle.classList.add('jedi-properties-toggle')

    toggle.addEventListener('click', () => {
      if (config.propertiesContainer.open) {
        config.propertiesContainer.close()
      } else {
        config.propertiesContainer.showModal()
        config.propertiesContainer.focus()
      }
    })

    return toggle
  }

  getQuickAddPropertyToggle (config) {
    const toggle = this.getButton(config)
    toggle.classList.add('jedi-quick-add-property-toggle')

    toggle.addEventListener('click', () => {
      if (config.propertiesContainer.open) {
        config.propertiesContainer.close()
      } else {
        config.propertiesContainer.showModal()
      }
    })

    return toggle
  }

  /**
   * Container that will collapse and expand to show and hide it contents
   */
  getCollapse (config) {
    const collapse = document.createElement('div')
    collapse.classList.add('jedi-collapse')
    collapse.setAttribute('id', config.id)

    if (this.useToggleEvents && config.startCollapsed) {
      collapse.style.display = 'none'
    }

    return collapse
  }

  /**
   * Toggle button for collapse
   */
  getCollapseToggle (config) {
    const toggle = this.getButton(config)
    toggle.classList.add('jedi-collapse-toggle')
    toggle.setAttribute('always-enabled', '')

    if (this.useToggleEvents) {
      toggle.addEventListener('click', () => {
        if (config.collapse.style.display === 'none') {
          config.collapse.style.display = 'block'
        } else {
          config.collapse.style.display = 'none'
        }
      })
    }

    toggle.style.transition = 'transform 0.1s ease'

    if (config.startCollapsed) {
      toggle.classList.add('collapsed')
    }

    const syncState = () => {
      const collapsed = toggle.classList.contains('collapsed')
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true')
      toggle.style.transform = collapsed ? 'rotate(90deg)' : 'rotate(0deg)'
    }

    syncState()

    if (this.useToggleEvents) {
      toggle.addEventListener('click', () => toggle.classList.toggle('collapsed'))
    }

    new MutationObserver(syncState).observe(toggle, { attributes: true, attributeFilter: ['class'] })

    return toggle
  }

  /**
   * Makes the whole editor header (legend) toggle the collapse, so users can
   * click the title or empty header area instead of only the small toggle
   * button. Clicks on interactive header items (buttons, links, form controls,
   * actions and switcher slots) are ignored so they keep their own behavior.
   */
  makeHeaderClickable (legend, collapseToggle) {
    legend.style.cursor = 'pointer'
    legend.addEventListener('click', (event) => {
      if (event.target.closest(
        'button, a, input, select, textarea, [contenteditable], ' +
        '.jedi-actions-slot, .jedi-switcher-slot'
      )) {
        return
      }
      collapseToggle.click()
    })
  }

  /**
   * Container for properties editing elements like property activators
   */
  getPropertiesSlot (config) {
    const html = this.getDialog()
    html.classList.add('jedi-properties-slot')
    html.setAttribute('id', config.id)

    html.addEventListener('click', (event) => {
      if (event.target === html) {
        html.close()
      }
    })

    return html
  }

  getQuickAddPropertySlot (config) {
    const html = this.getDialog()
    html.classList.add('jedi-quick-add-property-slot')
    html.setAttribute('id', config.id)

    html.addEventListener('click', (event) => {
      if (event.target === html) {
        html.close()
      }
    })

    return html
  }

  /**
   * Container for properties editing elements like property activators
   */
  getJsonData (config) {
    const dialog = this.getDialog()
    dialog.classList.add('jedi-json-data')
    dialog.setAttribute('id', config.id)

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        dialog.close()
      }
    })

    // toggle
    const toggle = this.getButton({
      // content: config.propertiesToggleContent, // todo: use text config or something
      id: 'jedi-json-data-toggle-' + config.id,
      icon: 'edit'
    })

    toggle.classList.add('jedi-json-data-toggle')

    toggle.addEventListener('click', () => {
      if (dialog.open) {
        dialog.close()
      } else {
        dialog.showModal()
      }
    })

    const control = document.createElement('div')

    // label
    const { label } = this.getLabel({
      for: 'json-data-input-' + config.id,
      text: 'JSON Data'
    })

    // input
    const input = document.createElement('textarea')
    input.setAttribute('id', 'json-data-input-' + config.id)
    input.cols = 60
    input.style.whiteSpace = 'pre'
    input.style.overflowX = 'auto'
    input.style.resize = 'both'
    input.style.maxHeight = '60vh'

    // copyBtn
    const copyBtn = this.getButton({
      id: 'jedi-json-data-copy-' + config.id,
      icon: 'copy'
    })
    copyBtn.classList.add('jedi-json-data-copy')

    // saveBtn
    const saveBtn = this.getButton({
      // content: config.propertiesToggleContent, // todo: use text config or something
      id: 'jedi-json-data-save-' + config.id,
      icon: 'save'
    })

    dialog.appendChild(control)
    control.appendChild(label)
    control.appendChild(input)
    dialog.appendChild(copyBtn)
    dialog.appendChild(saveBtn)

    return {
      dialog,
      toggle,
      control,
      input,
      copyBtn,
      saveBtn
    }
  }

  /**
   * Container for screen reader announced messages
   */
  getPropertiesAriaLive () {
    const html = document.createElement('div')
    html.classList.add('jedi-properties-aria-live')
    html.setAttribute('role', 'status')
    html.setAttribute('aria-live', 'polite')
    return html
  }

  /**
   * A message that will be announced by screen reader
   */
  getAriaLiveMessage (message) {
    const html = document.createElement('p')
    html.classList.add('jedi-aria-live-message')
    html.textContent = message
    this.visuallyHidden(html)
    return html
  }

  /**
   * Wrapper for property activators
   */
  getPropertiesActivators () {
    const html = document.createElement('div')
    html.classList.add('jedi-properties-activators')
    return html
  }

  /**
   * Group for property activators
   */
  getPropertiesGroup (config = {}) {
    if (config.accordion && config.name) {
      const id = 'jedi-prop-group-' + config.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
      const item = this.getAccordionItem({ title: config.name, id })

      return { container: item.container, group: item.body, name: item.toggle }
    }

    const container = document.createElement('div')
    container.classList.add('jedi-properties-group-container')

    const group = document.createElement('div')
    group.classList.add('jedi-properties-group')

    const name = document.createElement('p')
    name.classList.add('jedi-properties-group-name')
    name.textContent = config.name ?? ''

    container.appendChild(name)
    container.appendChild(group)
    return { container, group, name }
  }

  /**
   * Wrapper buttons
   */
  getBtnGroup () {
    const html = document.createElement('span')
    html.classList.add('jedi-btn-group')
    return html
  }

  /**
   * A button
   */
  getButton (config = {}) {
    const button = document.createElement('button')
    const text = document.createElement('span')
    const icon = document.createElement('i')

    button.classList.add('jedi-btn')
    button.setAttribute('type', 'button')

    if (config.value) {
      button.value = config.value
    }

    if (config.id) {
      button.setAttribute('id', config.id)
    }

    text.textContent = ' ' + config.content

    if (this.btnIcons && this.icons && config.icon) {
      this.addIconClass(icon, this.icons[config.icon])
      icon.setAttribute('title', config.content)
    }

    if (!this.btnContents) {
      this.visuallyHidden(text)
    }

    if (this.icons && config.icon) {
      button.appendChild(icon)
    }

    button.appendChild(text)

    return button
  }

  /**
   * A schema-defined action button (x-buttons keyword).
   *
   * This method only renders: the label is expected to be already sanitized by
   * the editor (Editor.purifyContent(), decision 9c) and the attributes are
   * expected to be already filtered against the allowlist by the editor
   * (utils.filterAttributes(), decision 6a). The theme does not sanitize or
   * filter. No theme styling is applied, only the unstyled `jedi-x-button` hook
   * class (decision 4).
   * @param {object} config - Button config
   * @param {string} [config.label] - Pre-sanitized HTML label
   * @param {object} [config.attributes] - Pre-filtered HTML attributes
   * @return {HTMLButtonElement}
   */
  getXButton (config = {}) {
    const button = document.createElement('button')
    const label = document.createElement('span')

    button.classList.add('jedi-x-button')
    button.setAttribute('type', 'button')

    label.classList.add('jedi-x-button-label')
    // config.label is pre-sanitized HTML (decision 9c); safe to assign as innerHTML.
    label.innerHTML = config.label ?? ''
    button.appendChild(label)

    const attributes = isObject(config.attributes) ? config.attributes : {}

    for (const [key, value] of Object.entries(attributes)) {
      if (key === 'class') {
        String(value).split(' ').forEach((cls) => {
          if (cls) {
            button.classList.add(cls)
          }
        })
      } else {
        button.setAttribute(key, value)
      }
    }

    return button
  }

  getAddPropertyButton (config) {
    const html = this.getButton(config)
    html.classList.add('jedi-add-property-btn')
    return html
  }

  /**
   * Array "add" item button
   */
  getArrayBtnAdd (config) {
    const html = this.getButton({
      content: config.content,
      icon: 'add'
    })
    html.classList.add('jedi-array-add')
    return html
  }

  /**
   * Array "delete all" button
   */
  getArrayBtnDeleteAll (config) {
    const html = this.getButton({
      content: config.content,
      icon: 'delete'
    })
    html.classList.add('jedi-array-delete-all')
    return html
  }

  /**
   * Array "add after" item button
   */
  getAddAfterItemBtn (config) {
    const addAfterItemBtn = this.getButton({
      content: config.content,
      icon: 'add'
    })
    addAfterItemBtn.classList.add('jedi-array-add-after')
    return addAfterItemBtn
  }

  /**
   * Array "delete" item button
   */
  getDeleteItemBtn (config) {
    const deleteItemBtn = this.getButton({
      content: config.content,
      icon: 'delete'
    })
    deleteItemBtn.classList.add('jedi-array-delete')
    return deleteItemBtn
  }

  /**
   * Array "move up" item button
   */
  getMoveUpItemBtn (config) {
    const moveUpItemBtn = this.getButton({
      content: config.content,
      icon: 'moveUp'
    })
    moveUpItemBtn.classList.add('jedi-array-move-up')
    return moveUpItemBtn
  }

  /**
   * Array "move down" item button
   */
  getMoveDownItemBtn (config) {
    const moveDownItemBtn = this.getButton({
      content: config.content,
      icon: 'moveDown'
    })
    moveDownItemBtn.classList.add('jedi-array-move-down')
    return moveDownItemBtn
  }

  getDragItemBtn (config) {
    const dragItemBtn = this.getButton({
      content: config.content,
      icon: 'drag'
    })
    dragItemBtn.classList.add('jedi-array-drag')
    return dragItemBtn
  }

  /**
   * Wrapper for the editor description
   */
  getDescription (config = {}) {
    const description = document.createElement('small')
    description.style.display = 'block'
    description.classList.add('jedi-description')

    if (config.content) {
      description.innerHTML = config.content
    }

    if (config.id) {
      description.setAttribute('id', config.id)
    }

    return description
  }

  /**
   * Info button to display extra information
   */
  getInfo (config = {}) {
    const container = document.createElement('span')
    const info = document.createElement('a')
    const infoText = document.createElement('span')
    const icon = document.createElement('i')

    container.classList.add('jedi-info-button-container')
    container.style.display = 'inline-block'

    info.setAttribute('href', '#')
    info.classList.add('jedi-info-button')

    if (isObject(config.attributes)) {
      for (const [key, value] of Object.entries(config.attributes)) {
        info.setAttribute(key, value)
      }
    }

    infoText.textContent = 'Info'

    if (!this.btnContents && this.btnIcons) {
      this.visuallyHidden(infoText)
    }

    icon.setAttribute('title', 'More information')

    if (this.icons) {
      this.addIconClass(icon, this.icons['info'])
    }

    info.appendChild(icon)
    info.appendChild(infoText)
    container.appendChild(info)

    return { container, info }
  }

  /**
   * Creates a base native <dialog> element with shared styling
   */
  getDialog () {
    const dialog = document.createElement('dialog')
    dialog.classList.add('jedi-modal-dialog')
    dialog.style.border = '1px solid #6c757d'
    dialog.style.borderRadius = '4px'
    dialog.style.minWidth = '400px'
    dialog.style.maxWidth = '90vw'
    return dialog
  }

  /**
   * Dialog or modal that contains extra information about the control
   */
  infoAsModal (info, id, config = {}) {
    const dialog = this.getDialog()
    const title = document.createElement('div')
    const content = document.createElement('div')
    const closeBtn = this.getButton({
      content: 'Close',
      icon: 'close'
    })

    dialog.setAttribute('id', id + '-modal')

    title.classList.add('jedi-modal-title')

    if (isString(config.title)) {
      title.innerHTML = config.title
    }

    content.classList.add('jedi-modal-content')

    if (isString(config.content)) {
      content.innerHTML = config.content
    }

    closeBtn.classList.add('jedi-modal-close')
    closeBtn.setAttribute('always-enabled', '')

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        dialog.close()
      }
    })

    closeBtn.addEventListener('click', () => {
      dialog.close()
    })

    info.info.addEventListener('click', () => {
      dialog.showModal()
    })

    info.container.appendChild(dialog)
    dialog.appendChild(title)
    dialog.appendChild(content)
    dialog.appendChild(closeBtn)
  }

  getPlaceholderControl (config) {
    const descriptionId = config.id + '-description'
    const messagesId = config.id + '-messages'

    const container = document.createElement('div')
    const placeholder = document.createElement('div')
    const actions = this.getActionsSlot()
    const info = this.getInfo(config.info)
    const { label, labelText } = this.getLabel({
      for: config.id,
      text: config.title,
      visuallyHidden: config.titleHidden,
      titleIconClass: config.titleIconClass
    })
    const description = this.getDescription({
      content: config.description,
      id: descriptionId
    })
    const messages = this.getMessagesSlot({
      id: messagesId
    })

    const switcherSlot = this.getSwitcherSlot()

    if (config?.info?.variant === 'modal') {
      this.infoAsModal(info, config.id, config.info)
    }

    container.appendChild(label)

    if (isObject(config.info)) {
      container.appendChild(info.container)
    }

    container.appendChild(switcherSlot)
    container.appendChild(placeholder)
    container.appendChild(description)
    container.appendChild(messages)
    container.appendChild(actions)

    return { container, placeholder, label, info, labelText, description, messages, actions, switcherSlot }
  }

  /**
   * Object control is a card containing multiple editors.
   * Each editor is mapped to an object instance property.
   * Properties can be added, activated and deactivated depending on configuration
   */
  getObjectControl (config) {
    const collapseId = 'collapse-' + config.id

    const container = document.createElement('div')
    const actions = this.getActionsSlot()
    const body = this.getCardBody()
    const ariaLive = this.getPropertiesAriaLive()
    const messages = this.getMessagesSlot()
    const childrenSlot = this.getChildrenSlot()

    if (config.isAccordion || config.isAccordionProperties) {
      childrenSlot.id = 'accordion-' + config.id
    }

    const propertiesActivators = this.getPropertiesActivators()
    const info = this.getInfo(config.info)
    const description = this.getDescription({
      content: config.description
    })
    const jsonData = this.getJsonData({
      id: 'json-data-' + config.id
    })
    const propertiesContainer = this.getPropertiesSlot({
      id: 'properties-slot-' + config.id
    })
    const propertiesToggle = this.getPropertiesToggle({
      content: config.propertiesToggleContent,
      id: 'properties-slot-toggle-' + config.id,
      icon: 'properties',
      propertiesContainer: propertiesContainer
    })
    const collapse = this.getCollapse({
      id: collapseId,
      startCollapsed: config.startCollapsed
    })
    const collapseToggle = this.getCollapseToggle({
      content: config.collapseToggleContent,
      id: 'collapse-toggle-' + config.id,
      icon: 'collapse',
      collapseId: collapseId,
      collapse: collapse,
      startCollapsed: config.startCollapsed
    })
    const quickAddPropertyContainer = this.getQuickAddPropertySlot({
      id: 'quick-add-property-slot-' + config.id
    })
    const quickAddPropertyControl = this.getInputControl({
      type: 'text',
      id: 'jedi-quick-add-property-input-' + config.id,
      title: config.addPropertyContent
    })
    const quickAddPropertyBtn = this.getAddPropertyButton({
      content: config.addPropertyContent,
      icon: 'add'
    })
    const quickAddPropertyToggle = this.getQuickAddPropertyToggle({
      content: config.addPropertyContent,
      icon: 'add',
      propertiesContainer: quickAddPropertyContainer
    })
    const fieldset = this.getFieldset()
    const { legend, left, infoContainer, legendText, right } = this.getLegend({
      content: config.title,
      id: config.id,
      titleHidden: config.titleHidden,
      titleIconClass: config.titleIconClass
    })

    if (config?.info?.variant === 'modal') {
      this.infoAsModal(info, config.id, config.info)
    }

    container.appendChild(fieldset)
    container.appendChild(propertiesContainer)
    container.appendChild(quickAddPropertyContainer)

    if (config.addProperty) {
      quickAddPropertyContainer.appendChild(quickAddPropertyControl.container)
      quickAddPropertyContainer.appendChild(quickAddPropertyBtn)
    }

    if (config.editJsonData) {
      container.appendChild(jsonData.dialog)
    }

    fieldset.appendChild(legend)

    if (isObject(config.info)) {
      while (info.container.firstChild) {
        infoContainer.appendChild(info.container.firstChild)
      }
    }

    fieldset.appendChild(collapse)
    collapse.appendChild(body)

    if (config.description) {
      body.appendChild(description)
    }

    body.appendChild(messages)

    const switcherSlot = this.getSwitcherSlot()
    left.appendChild(switcherSlot)

    if (config.readOnly === false) {
      right.appendChild(actions)
    }

    body.appendChild(childrenSlot)

    if (config.editJsonData) {
      actions.appendChild(jsonData.toggle)
    }

    if (config.addProperty) {
      actions.appendChild(quickAddPropertyToggle)
    }

    if (config.enablePropertiesToggle) {
      actions.appendChild(propertiesToggle)
      propertiesContainer.appendChild(ariaLive)
      propertiesContainer.appendChild(propertiesActivators)
    }

    if (config.enableCollapseToggle) {
      actions.appendChild(collapseToggle)
      this.makeHeaderClickable(legend, collapseToggle)
    }

    return {
      container,
      collapse,
      collapseToggle,
      description,
      body,
      actions,
      messages,
      childrenSlot,
      propertiesToggle,
      jsonData,
      propertiesContainer,
      quickAddPropertyContainer,
      quickAddPropertyControl,
      quickAddPropertyBtn,
      quickAddPropertyToggle,
      ariaLive,
      propertiesActivators,
      legend,
      legendText,
      infoContainer,
      right,
      switcherSlot
    }
  }

  /**
   * Flat variant of getObjectControl — same interface but no fieldset/legend/collapse/panel-body.
   * Children render directly in a plain container div.
   */
  getObjectControlFlat (config) {
    const container = document.createElement('div')
    const actions = this.getActionsSlot()
    const body = document.createElement('div')
    const ariaLive = this.getPropertiesAriaLive()
    const messages = this.getMessagesSlot()
    const childrenSlot = this.getChildrenSlot()

    if (config.isAccordion || config.isAccordionProperties) {
      childrenSlot.id = 'accordion-' + config.id
    }

    const propertiesActivators = this.getPropertiesActivators()
    const info = this.getInfo(config.info)
    const description = this.getDescription({ content: config.description })
    const jsonData = this.getJsonData({ id: 'json-data-' + config.id })
    const propertiesContainer = this.getPropertiesSlot({ id: 'properties-slot-' + config.id })
    const propertiesToggle = this.getPropertiesToggle({
      content: config.propertiesToggleContent,
      id: 'properties-slot-toggle-' + config.id,
      icon: 'properties',
      propertiesContainer: propertiesContainer
    })
    const quickAddPropertyContainer = this.getQuickAddPropertySlot({ id: 'quick-add-property-slot-' + config.id })
    const quickAddPropertyControl = this.getInputControl({
      type: 'text',
      id: 'jedi-quick-add-property-input-' + config.id,
      title: config.addPropertyContent
    })
    const quickAddPropertyBtn = this.getAddPropertyButton({ content: config.addPropertyContent, icon: 'add' })
    const quickAddPropertyToggle = this.getQuickAddPropertyToggle({
      content: config.addPropertyContent,
      icon: 'add',
      propertiesContainer: quickAddPropertyContainer
    })

    // Stubs for interface compatibility (not rendered as panel chrome)
    const collapse = document.createElement('div')
    const collapseToggle = document.createElement('div')
    const infoContainer = document.createElement('div')
    const switcherSlot = this.getSwitcherSlot()

    // Header row mirrors card legend structure so buttons have a visual anchor
    const legend = document.createElement('div')
    legend.classList.add('jedi-editor-legend')
    legend.style.display = 'flex'
    legend.style.justifyContent = 'space-between'
    legend.style.alignItems = 'center'
    const left = document.createElement('div')
    left.classList.add('jedi-editor-legend-left')
    const right = document.createElement('div')
    right.classList.add('jedi-editor-legend-right')
    right.style.display = 'flex'
    right.style.alignItems = 'center'
    legend.appendChild(left)
    legend.appendChild(right)

    const legendText = document.createElement('label')
    legendText.classList.add('jedi-title', 'jedi-legend')
    legendText.textContent = config.title || ''
    if (config.titleHidden) {
      this.visuallyHidden(legendText)
    }
    left.appendChild(legendText)
    left.appendChild(switcherSlot)

    if (config?.info?.variant === 'modal') {
      this.infoAsModal(info, config.id, config.info)
    }

    if (config.editJsonData) {
      container.appendChild(jsonData.dialog)
    }

    const innerWrapper = document.createElement('div')
    innerWrapper.appendChild(legend)
    container.appendChild(innerWrapper)

    if (config.addProperty) {
      quickAddPropertyContainer.appendChild(quickAddPropertyControl.container)
      quickAddPropertyContainer.appendChild(quickAddPropertyBtn)
    }

    if (config.description) {
      body.appendChild(description)
    }

    body.appendChild(messages)

    if (config.readOnly === false) {
      right.appendChild(actions)
    }

    body.appendChild(childrenSlot)
    innerWrapper.appendChild(body)
    innerWrapper.appendChild(propertiesContainer)
    innerWrapper.appendChild(quickAddPropertyContainer)

    if (config.editJsonData) {
      actions.appendChild(jsonData.toggle)
    }

    if (config.addProperty) {
      actions.appendChild(quickAddPropertyToggle)
    }

    if (config.enablePropertiesToggle) {
      actions.appendChild(propertiesToggle)
      propertiesContainer.appendChild(ariaLive)
      propertiesContainer.appendChild(propertiesActivators)
    }

    if (config.enableCollapseToggle) {
      actions.appendChild(collapseToggle)
    }

    return {
      container,
      collapse,
      collapseToggle,
      description,
      body,
      actions,
      messages,
      childrenSlot,
      propertiesToggle,
      jsonData,
      propertiesContainer,
      quickAddPropertyContainer,
      quickAddPropertyControl,
      quickAddPropertyBtn,
      quickAddPropertyToggle,
      ariaLive,
      propertiesActivators,
      legend,
      legendText,
      infoContainer,
      right,
      switcherSlot,
      innerWrapper
    }
  }

  // eslint-disable-next-line no-unused-vars
  initHorizontalObject (container) {}

  // eslint-disable-next-line no-unused-vars
  adaptForHorizontalInputControl (control, labelCol, inputCol) {}

  // eslint-disable-next-line no-unused-vars
  adaptForHorizontalTextareaControl (control, labelCol, inputCol) {}

  // eslint-disable-next-line no-unused-vars
  adaptForHorizontalSelectControl (control, labelCol, inputCol) {}

  // eslint-disable-next-line no-unused-vars
  adaptForHorizontalCheckboxControl (control, labelCol, inputCol) {}

  // eslint-disable-next-line no-unused-vars
  adaptForHorizontalRadiosControl (control, labelCol, inputCol) {}

  // eslint-disable-next-line no-unused-vars
  adaptForHorizontalCheckboxesControl (control, labelCol, inputCol) {}

  // eslint-disable-next-line no-unused-vars
  adaptForHorizontalArrayControl (control, labelCol, inputCol, title) {}

  // eslint-disable-next-line no-unused-vars
  adaptForHorizontalObjectControl (control, labelCol, inputCol, title) {}

  // eslint-disable-next-line no-unused-vars
  adaptForHorizontalMultipleControl (control, labelCol, inputCol, title) {}

  /**
   * Returns an accordion item wrapping a child editor.
   * Used by EditorObjectAccordionProperties to wrap each property.
   */
  getAccordionItem (config) {
    const container = document.createElement('div')
    container.classList.add('jedi-accordion-item')

    const header = document.createElement('div')
    header.classList.add('jedi-accordion-header')

    const toggle = document.createElement('button')
    toggle.type = 'button'
    toggle.classList.add('jedi-accordion-toggle', 'collapsed')

    const chevron = document.createElement('i')
    chevron.classList.add('jedi-accordion-chevron')
    if (this.icons && this.icons['collapse']) {
      this.addIconClass(chevron, this.icons['collapse'])
    } else {
      chevron.textContent = '▾'
    }
    chevron.style.display = 'inline-block'
    chevron.style.transition = 'transform 0.1s ease'
    chevron.style.marginRight = '0.5em'

    const titleSpan = document.createElement('span')
    titleSpan.style.marginRight = '0.5em'
    titleSpan.textContent = config.title

    toggle.appendChild(chevron)
    toggle.appendChild(titleSpan)

    const collapse = document.createElement('div')
    collapse.classList.add('jedi-accordion-collapse')
    collapse.style.display = 'none'

    const body = document.createElement('div')
    body.classList.add('jedi-accordion-body')

    const syncState = () => {
      const collapsed = toggle.classList.contains('collapsed')
      chevron.style.transform = collapsed ? 'rotate(0deg)' : 'rotate(-180deg)'
    }

    syncState()

    if (this.useToggleEvents) {
      toggle.addEventListener('click', () => {
        if (collapse.style.display === 'none') {
          collapse.style.display = 'block'
        } else {
          collapse.style.display = 'none'
        }
        toggle.classList.toggle('collapsed')
      })
    }

    new MutationObserver(syncState).observe(toggle, { attributes: true, attributeFilter: ['class'] })

    header.appendChild(toggle)
    collapse.appendChild(body)
    container.appendChild(header)
    container.appendChild(collapse)

    return { container, header, toggle, collapse, body }
  }

  /**
   * Array control is a card containing multiple editors.
   * Items can bve added, deleted or moved up or down.
   */
  getArrayControl (config) {
    const collapseId = 'collapse-' + config.id

    const container = document.createElement('div')
    const actions = this.getActionsSlot()
    const body = this.getCardBody()
    const messages = this.getMessagesSlot()
    const childrenSlot = this.getChildrenSlot()
    const btnGroup = this.getBtnGroup()
    const addBtn = this.getArrayBtnAdd({
      content: config.arrayAddContent
    })
    const footerAddBtn = this.getArrayBtnAdd({
      content: config.arrayFooterAddContent
    })
    const deleteAllBtn = config.arrayDeleteAll === true
      ? this.getArrayBtnDeleteAll({ content: config.arrayDeleteAllContent })
      : null
    const footerDeleteAllBtn = config.arrayFooterDeleteAll === true
      ? this.getArrayBtnDeleteAll({ content: config.arrayFooterDeleteAllContent })
      : null
    const footerBtnGroup = this.getBtnGroup()
    const footer = this.getArrayFooter()

    const fieldset = this.getFieldset()
    const info = this.getInfo(config.info)
    const { legend, left, legendText, infoContainer, right } = this.getLegend({
      content: config.title,
      id: config.id,
      titleHidden: config.titleHidden,
      titleIconClass: config.titleIconClass
    })
    const description = this.getDescription({
      content: config.description
    })
    const jsonData = this.getJsonData({
      id: 'json-data-' + config.id
    })
    const collapse = this.getCollapse({
      id: collapseId,
      startCollapsed: config.startCollapsed
    })
    const collapseToggle = this.getCollapseToggle({
      content: config.collapseToggleContent,
      id: 'collapse-toggle-' + config.id,
      icon: 'collapse',
      collapseId: collapseId,
      collapse: collapse,
      startCollapsed: config.startCollapsed
    })

    if (config?.info?.variant === 'modal') {
      this.infoAsModal(info, config.id, config.info)
    }

    container.appendChild(fieldset)

    if (config.editJsonData) {
      container.appendChild(jsonData.dialog)
    }

    fieldset.appendChild(legend)

    if (isObject(config.info)) {
      while (info.container.firstChild) {
        infoContainer.appendChild(info.container.firstChild)
      }
    }

    fieldset.appendChild(collapse)
    collapse.appendChild(body)

    if (config.description) {
      body.appendChild(description)
    }

    body.appendChild(messages)

    const switcherSlot = this.getSwitcherSlot()
    left.appendChild(switcherSlot)

    if (config.readOnly === false) {
      right.appendChild(actions)
    }

    actions.appendChild(btnGroup)

    if (config.editJsonData) {
      btnGroup.appendChild(jsonData.toggle)
    }

    if (deleteAllBtn) {
      btnGroup.appendChild(deleteAllBtn)
    }

    if (isSet(config.arrayAdd) && config.arrayAdd === true) {
      btnGroup.appendChild(addBtn)
    }

    body.appendChild(childrenSlot)

    if (config.enableCollapseToggle) {
      actions.appendChild(collapseToggle)
      this.makeHeaderClickable(legend, collapseToggle)
    }

    const showFooter = (
      (config.arrayFooterAdd === true || config.arrayFooterDeleteAll === true) &&
      config.readOnly === false
    )

    if (showFooter) {
      if (footerDeleteAllBtn) {
        footerBtnGroup.appendChild(footerDeleteAllBtn)
      }
      if (config.arrayFooterAdd === true) {
        footerBtnGroup.appendChild(footerAddBtn)
      }
      if (config.arrayFooterButtonsPosition === 'right') {
        footerBtnGroup.style.marginLeft = 'auto'
      }
      footer.appendChild(footerBtnGroup)
      collapse.appendChild(footer)
    }

    return {
      container,
      collapseToggle,
      collapse,
      body,
      actions,
      messages,
      childrenSlot,
      btnGroup,
      addBtn,
      jsonData,
      legend,
      legendText,
      infoContainer,
      switcherSlot,
      footerAddBtn,
      deleteAllBtn,
      footerDeleteAllBtn
    }
  }

  getArrayItem (config = {}) {
    const container = document.createElement('div')
    const body = document.createElement('div')
    const actions = this.getActionsSlot()
    const arrayActions = this.getArrayActionsSlot()

    actions.style.textAlign = 'right'

    container.classList.add('jedi-array-item')
    body.classList.add('jedi-array-item-body')

    if (isSet(config.index)) {
      container.setAttribute('jedi-array-item-index', config.index)
    }

    actions.appendChild(arrayActions)

    container.appendChild(actions)

    container.appendChild(body)

    return {
      container,
      actions,
      arrayActions,
      body
    }
  }

  /**
   * Multiple control is a card containing multiple editors options that can be
   * selected with a switcher control. Only one editor can be active/visible
   * at a time
   */
  getMultipleControl (config = {}) {
    const container = document.createElement('div')
    const header = document.createElement('div')
    const body = document.createElement('div')
    const messages = this.getMessagesSlot()
    const childrenSlot = this.getChildrenSlot()
    const randomId = generateRandomID(5)
    const knownSwitchers = ['select', 'radios', 'radios-inline', 'modal', 'select-inline']
    const switcherType = knownSwitchers.includes(config.switcher) ? config.switcher : 'select'

    let switcher

    if (switcherType === 'select') {
      switcher = this.getSwitcherSelect({
        values: config.switcherOptionValues,
        titles: config.switcherOptionsLabels,
        title: config.id + '-switcher',
        id: config.id + '-switcher' + '-' + randomId,
        label: config.id + '-switcher' + '-' + randomId,
        titleHidden: true,
        readOnly: config.readOnly,
        noSpacing: true
      })
    }

    if (switcherType === 'radios' || switcherType === 'radios-inline') {
      switcher = this.getSwitcherRadios({
        values: config.switcherOptionValues,
        titles: config.switcherOptionsLabels,
        title: config.id + '-switcher',
        id: config.id + '-switcher' + '-' + randomId,
        label: config.id + '-switcher' + '-' + randomId,
        titleHidden: true,
        readOnly: config.readOnly,
        inline: switcherType === 'radios-inline',
        noSpacing: true
      })
    }

    if (switcherType === 'modal') {
      switcher = this.getSwitcherModal({
        values: config.switcherOptionValues,
        titles: config.switcherOptionsLabels,
        id: config.id + '-switcher' + '-' + randomId,
        readOnly: config.readOnly
      })
    }

    if (switcherType === 'select-inline') {
      switcher = this.getSwitcherSelectInline({
        values: config.switcherOptionValues,
        titles: config.switcherOptionsLabels,
        id: config.id + '-switcher' + '-' + randomId,
        readOnly: config.readOnly
      })
    }

    switcher.container.classList.add('jedi-switcher')

    container.appendChild(header)
    container.appendChild(body)
    header.appendChild(switcher.container)
    body.appendChild(messages)
    body.appendChild(childrenSlot)

    return {
      container,
      header,
      body,
      messages,
      childrenSlot,
      switcher
    }
  }

  adaptForTableMultipleControl (control, td) {
  }

  getIfThenElseControl (config) {
    const container = document.createElement('div')
    const card = this.getCard()
    const actions = this.getActionsSlot()
    const body = this.getCardBody()
    const messages = this.getMessagesSlot()
    const childrenSlot = this.getChildrenSlot()
    const header = this.getCardHeader({
      content: config.title,
      titleHidden: config.titleHidden
    })
    const description = this.getDescription({
      content: config.description
    })

    body.appendChild(description)
    container.appendChild(messages)
    container.appendChild(childrenSlot)

    return {
      container,
      card,
      header,
      body,
      actions,
      messages,
      childrenSlot
    }
  }

  /**
   * Control for NullEditor
   */
  getNullControl (config) {
    const descriptionId = config.id + '-description'

    const container = document.createElement('div')
    const actions = this.getActionsSlot()
    const messages = this.getMessagesSlot()
    const br = document.createElement('br')
    const info = this.getInfo(config.info)
    const { label, labelText } = this.getFakeLabel({
      for: config.id,
      content: config.title,
      visuallyHidden: config.titleHidden,
      titleIconClass: config.titleIconClass
    })
    const description = this.getDescription({
      content: config.description,
      id: descriptionId
    })

    const switcherSlot = this.getSwitcherSlot()

    if (config?.info?.variant === 'modal') {
      this.infoAsModal(info, config.id, config.info)
    }

    container.appendChild(label)

    if (isObject(config.info)) {
      container.appendChild(info.container)
    }

    container.appendChild(switcherSlot)
    container.appendChild(br)
    container.appendChild(description)
    container.appendChild(messages)
    container.appendChild(actions)

    return { container, label, info, labelText, description, messages, actions, switcherSlot }
  }

  /**
   * A Textarea
   */
  getTextareaControl (config) {
    const descriptionId = config.id + '-description'
    const messagesId = config.id + '-messages'
    const describedBy = messagesId + ' ' + descriptionId

    const container = document.createElement('div')
    const actions = this.getActionsSlot()
    const input = document.createElement('textarea')
    const info = this.getInfo(config.info)
    const { label, labelText } = this.getLabel({
      for: config.id,
      text: config.title,
      visuallyHidden: config.titleHidden,
      titleIconClass: config.titleIconClass
    })
    const description = this.getDescription({
      content: config.description,
      id: descriptionId
    })
    const messages = this.getMessagesSlot({
      id: messagesId
    })

    const switcherSlot = this.getSwitcherSlot()

    input.setAttribute('aria-describedby', describedBy)
    input.setAttribute('id', config.id)
    input.setAttribute('name', config.id)
    input.style.width = '100%'

    if (config?.info?.variant === 'modal') {
      this.infoAsModal(info, config.id, config.info)
    }

    container.appendChild(label)

    if (isObject(config.info)) {
      container.appendChild(info.container)
    }

    container.appendChild(switcherSlot)
    container.appendChild(input)
    container.appendChild(description)
    container.appendChild(messages)
    container.appendChild(actions)

    return { container, input, label, info, labelText, description, messages, actions, switcherSlot }
  }

  adaptForTableTextareaControl (control) {
    this.visuallyHidden(control.label)
    this.visuallyHidden(control.description)
    control.input.setAttribute('rows', '1')
  }

  /**
   * An Input control
   */
  getInputControl (config) {
    const messagesId = config.id + '-messages'
    const descriptionId = config.id + '-description'
    const describedBy = messagesId + ' ' + descriptionId

    const container = document.createElement('div')
    const actions = this.getActionsSlot()
    const input = document.createElement('input')
    const info = this.getInfo(config.info)
    const { label, labelText } = this.getLabel({
      for: config.id,
      text: config.title,
      visuallyHidden: config.titleHidden,
      titleIconClass: config.titleIconClass
    })
    const description = this.getDescription({
      content: config.description,
      id: descriptionId
    })
    const messages = this.getMessagesSlot({
      id: messagesId
    })

    const switcherSlot = this.getSwitcherSlot()

    input.setAttribute('aria-describedby', describedBy)
    input.setAttribute('type', config.type)
    input.setAttribute('id', config.id)
    input.setAttribute('name', config.id)
    input.style.width = '100%'

    container.appendChild(label)

    if (config?.info?.variant === 'modal') {
      this.infoAsModal(info, config.id, config.info)
    }

    if (isObject(config.info)) {
      container.appendChild(info.container)
    }

    container.appendChild(switcherSlot)
    container.appendChild(input)
    container.appendChild(description)
    container.appendChild(messages)
    container.appendChild(actions)

    return { container, input, label, info, labelText, description, messages, actions, switcherSlot }
  }

  getInputRangeControl (config) {
    const control = this.getInputControl(config)

    // Create output element to display current value
    const output = document.createElement('output')
    output.className = 'range-output'
    output.style.marginLeft = '10px'
    output.style.fontWeight = 'bold'

    // Insert output after the input
    control.input.parentNode.insertBefore(output, control.input.nextSibling)

    return { ...control, output }
  }

  adaptForTableInputControl (control) {
    this.visuallyHidden(control.label)
    this.visuallyHidden(control.description)
  }

  /**
   * A radio group control
   */
  getRadiosControl (config) {
    const messagesId = config.id + '-messages'
    const descriptionId = config.id + '-description'

    const container = document.createElement('div')
    const fieldset = this.getRadioFieldset()
    const info = this.getInfo(config.info)
    const { legend, legendText } = this.getRadioLegend({
      content: config.title,
      id: config.id,
      for: config.id,
      titleIconClass: config.titleIconClass
    })
    const messages = this.getMessagesSlot({
      id: messagesId
    })
    const description = this.getDescription({
      content: config.description,
      id: descriptionId
    })

    if (config?.info?.variant === 'modal') {
      this.infoAsModal(info, config.id, config.info)
    }

    if (config.titleHidden) {
      this.visuallyHidden(legend)
    }

    const radioControls = []
    const radios = []
    const labels = []
    const labelTexts = []

    config.values.forEach((value, index) => {
      const describedBy = messagesId + ' ' + descriptionId

      const radioControl = document.createElement('div')
      const radio = document.createElement('input')
      const label = document.createElement('label')
      const labelText = document.createElement('span')

      radio.setAttribute('type', 'radio')
      radio.setAttribute('id', config.id + '-' + index)
      radio.setAttribute('name', config.id)
      radio.setAttribute('value', value)
      radio.setAttribute('aria-describedby', describedBy)

      label.setAttribute('for', config.id + '-' + index)
      label.classList.add('jedi-title')
      label.classList.add('jedi-label')

      labelTexts.push(labelText)

      if (isSet(config.titles) && isSet(config.titles[index])) {
        labelText.textContent = config.titles[index] ?? value
      }

      radioControls.push(radioControl)
      radios.push(radio)
      labels.push(label)
    })

    const switcherSlot = this.getSwitcherSlot()

    container.appendChild(fieldset)
    fieldset.appendChild(legend)
    legend.appendChild(switcherSlot)

    if (isObject(config.info)) {
      legendText.after(info.container)
    }

    radioControls.forEach((radioControl, index) => {
      fieldset.appendChild(radioControls[index])
      radioControl.appendChild(radios[index])
      radioControl.appendChild(labels[index])
      labels[index].appendChild(labelTexts[index])
    })

    fieldset.appendChild(description)
    fieldset.appendChild(messages)

    return {
      container,
      fieldset,
      legend,
      legendText,
      info,
      radios,
      labels,
      labelTexts,
      radioControls,
      description,
      messages,
      switcherSlot
    }
  }

  adaptForTableRadiosControl (control) {
    this.visuallyHidden(control.legend)
    this.visuallyHidden(control.description)
  }

  /**
   * A checkbox control
   */
  getCheckboxControl (config) {
    const descriptionId = config.id + '-description'
    const messagesId = config.id + '-messages'
    const describedBy = messagesId + ' ' + descriptionId

    const container = document.createElement('div')
    const actions = this.getActionsSlot()
    const formGroup = document.createElement('span')
    const input = document.createElement('input')
    const info = this.getInfo(config.info)
    const { label, labelText } = this.getLabel({
      for: config.id,
      text: config.title,
      visuallyHidden: config.titleHidden,
      titleIconClass: config.titleIconClass
    })
    const description = this.getDescription({
      content: config.description,
      id: descriptionId
    })
    const messages = this.getMessagesSlot({
      id: messagesId
    })

    input.setAttribute('type', 'checkbox')
    input.setAttribute('id', config.id)
    input.setAttribute('name', config.id)
    input.setAttribute('aria-describedby', describedBy)

    const switcherSlot = this.getSwitcherSlot()

    if (config?.info?.variant === 'modal') {
      this.infoAsModal(info, config.id, config.info)
    }

    container.appendChild(formGroup)
    container.appendChild(actions)
    formGroup.appendChild(input)
    formGroup.appendChild(label)

    if (isObject(config.info)) {
      formGroup.appendChild(info.container)
    }

    formGroup.appendChild(switcherSlot)
    formGroup.appendChild(description)
    formGroup.appendChild(messages)

    return { container, formGroup, input, label, info, labelText, description, messages, actions, switcherSlot }
  }

  adaptForTableCheckboxControl (control, td) {
    this.visuallyHidden(control.label)
    this.visuallyHidden(control.description)
  }

  getCheckboxesControl (config) {
    const messagesId = config.id + '-messages'
    const descriptionId = config.id + '-description'

    const container = document.createElement('div')
    const fieldset = this.getRadioFieldset()
    const info = this.getInfo(config.info)
    const { legend, legendText } = this.getRadioLegend({
      content: config.title,
      id: config.id,
      for: config.id,
      titleIconClass: config.titleIconClass
    })
    const messages = this.getMessagesSlot({
      id: messagesId
    })
    const description = this.getDescription({
      content: config.description,
      id: descriptionId
    })

    if (config.titleHidden) {
      this.visuallyHidden(legend)
    }

    const checkboxControls = []
    const checkboxes = []
    const labels = []
    const labelTexts = []

    config.values.forEach((value, index) => {
      const describedBy = messagesId + ' ' + descriptionId
      const checkboxId = config.id + '-' + index

      const checkboxControl = document.createElement('div')
      const checkbox = document.createElement('input')
      const label = document.createElement('label')
      const labelText = document.createElement('span')

      checkbox.setAttribute('type', 'checkbox')
      checkbox.setAttribute('id', checkboxId)
      checkbox.setAttribute('name', config.id)
      checkbox.setAttribute('value', value)
      checkbox.setAttribute('aria-describedby', describedBy)

      label.setAttribute('for', checkboxId)

      if (config.titles && config.titles[index]) {
        labelText.textContent = config.titles[index]
      }

      checkboxControls.push(checkboxControl)
      checkboxes.push(checkbox)
      labelTexts.push(labelText)
      labels.push(label)
    })

    if (config?.info?.variant === 'modal') {
      this.infoAsModal(info, config.id, config.info)
    }

    const switcherSlot = this.getSwitcherSlot()

    container.appendChild(fieldset)
    fieldset.appendChild(legend)
    legend.appendChild(switcherSlot)

    if (isObject(config.info)) {
      legendText.after(info.container)
    }

    checkboxControls.forEach((checkboxControl, index) => {
      fieldset.appendChild(checkboxControls[index])
      checkboxControl.appendChild(checkboxes[index])
      checkboxControl.appendChild(labels[index])
      labels[index].appendChild(labelTexts[index])
    })

    fieldset.appendChild(description)
    fieldset.appendChild(messages)

    return {
      container,
      fieldset,
      legend,
      legendText,
      checkboxes,
      labels,
      labelTexts,
      checkboxControls,
      description,
      messages,
      switcherSlot
    }
  }

  adaptForTableCheckboxesControl (control, td) {
    this.visuallyHidden(control.legend)
    this.visuallyHidden(control.description)
  }

  /**
   * A select control
   */
  getSelectControl (config) {
    const descriptionId = config.id + '-description'
    const messagesId = config.id + '-messages'
    const describedBy = messagesId + ' ' + descriptionId

    const container = document.createElement('div')
    const actions = this.getActionsSlot()
    const input = document.createElement('select')
    const info = this.getInfo(config.info)
    const { label, labelText } = this.getLabel({
      for: config.id,
      text: config.title,
      visuallyHidden: config.titleHidden,
      titleIconClass: config.titleIconClass
    })
    const messages = this.getMessagesSlot({
      id: messagesId
    })
    const description = this.getDescription({
      content: config.description,
      id: descriptionId
    })

    input.setAttribute('id', config.id)
    input.setAttribute('name', config.id)
    input.setAttribute('aria-describedby', describedBy)

    config.values.forEach((value, index) => {
      const option = document.createElement('option')
      option.setAttribute('value', value)

      if (config.titles && config.titles[index]) {
        option.textContent = config.titles[index]
      }

      input.appendChild(option)
    })

    const switcherSlot = this.getSwitcherSlot()

    if (config?.info?.variant === 'modal') {
      this.infoAsModal(info, config.id, config.info)
    }

    container.appendChild(label)

    if (isObject(config.info)) {
      container.appendChild(info.container)
    }

    container.appendChild(switcherSlot)
    container.appendChild(input)
    container.appendChild(description)
    container.appendChild(messages)
    container.appendChild(actions)

    return { container, input, label, info, labelText, description, messages, actions, switcherSlot }
  }

  adaptForTableSelectControl (control) {
    this.visuallyHidden(control.label)
    this.visuallyHidden(control.description)
  }

  /**
   * Control to switch between multiple editors options
   */
  getSwitcherSelect (config) {
    return this.getSelectControl(config)
  }

  /**
   * Control to switch between multiple editors options
   */
  getSwitcherRadios (config) {
    return this.getRadiosControl(config)
  }

  /**
   * Compact badge-button trigger that opens a modal to switch between multiple editors options
   */
  getSwitcherModal (config) {
    const container = document.createElement('span')
    const trigger = document.createElement('span')
    const dialog = this.getDialog()
    const dialogBody = document.createElement('div')
    const optionButtons = []

    const triggerText = document.createElement('span')
    const triggerIcon = document.createElement('i')

    container.classList.add('jedi-switcher-modal')
    trigger.classList.add('jedi-switcher-modal-trigger')
    trigger.setAttribute('role', 'button')
    trigger.setAttribute('tabindex', '0')
    trigger.setAttribute('aria-haspopup', 'dialog')
    trigger.setAttribute('aria-label', 'Switch type')

    trigger.appendChild(triggerText)

    if (this.icons && this.icons.switcher) {
      this.addIconClass(triggerIcon, this.icons.switcher)
      trigger.appendChild(document.createTextNode(' '))
      trigger.appendChild(triggerIcon)
    }

    dialogBody.classList.add('jedi-modal-content')

    config.values.forEach((value, index) => {
      const btn = document.createElement('button')
      btn.setAttribute('type', 'button')
      btn.setAttribute('aria-label', `Select: ${config.titles[index]}`)
      btn.textContent = config.titles[index]
      btn.dataset.switcherValue = value
      btn.classList.add('jedi-switcher-option-btn')
      optionButtons.push(btn)
      dialogBody.appendChild(btn)
    })

    trigger.addEventListener('click', () => {
      dialog.showModal()
    })

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        dialog.showModal()
      }
    })

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        dialog.close()
      }
    })

    container.appendChild(trigger)
    container.appendChild(dialog)
    dialog.appendChild(dialogBody)

    return { container, trigger, triggerText, dialog, dialogBody, optionButtons }
  }

  setSwitcherOptionActive (btn, active) {
    btn.classList.toggle('jedi-switcher-option-active', active)
  }

  /**
   * Compact inline <select> to switch between multiple editors options (no dialog)
   */
  getSwitcherSelectInline (config) {
    const container = document.createElement('span')
    const input = document.createElement('select')

    container.classList.add('jedi-switcher-select-inline')
    container.style.display = 'inline-block'

    input.classList.add('jedi-switcher-select-inline-input')
    input.style.width = 'auto'
    input.setAttribute('aria-label', 'Switch type')

    if (config.readOnly) {
      input.setAttribute('disabled', '')
    }

    config.values.forEach((value, index) => {
      const option = document.createElement('option')
      option.setAttribute('value', value)
      if (config.titles && config.titles[index]) {
        option.textContent = config.titles[index]
      }
      input.appendChild(option)
    })

    container.appendChild(input)

    return { container, input }
  }

  /**
   * Another type of error message container used for more complex editors like
   * object, array and multiple editors
   */
  getAlert (config) {
    return this.getErrorFeedback(config)
  }

  /**
   * Error messages
   * @public
   */
  getErrorFeedback (config) {
    const html = document.createElement('div')
    const invalidFeedbackText = document.createElement('small')
    const invalidFeedbackIcon = document.createElement('span')

    invalidFeedbackText.textContent = config.message
    invalidFeedbackIcon.textContent = '⚠ '
    invalidFeedbackIcon.setAttribute('aria-hidden', 'true')

    html.classList.add('jedi-error-message')

    html.appendChild(invalidFeedbackIcon)
    html.appendChild(invalidFeedbackText)
    return html
  }

  /**
   * Error messages
   * @public
   */
  getWarningFeedback (config) {
    const html = document.createElement('div')
    const invalidFeedbackText = document.createElement('small')
    const invalidFeedbackIcon = document.createElement('span')

    invalidFeedbackText.textContent = config.message
    invalidFeedbackIcon.textContent = '⚠ '
    invalidFeedbackIcon.classList.add('jedi-warning-message')
    invalidFeedbackIcon.setAttribute('aria-hidden', 'true')

    html.classList.add('jedi-warning-message')

    html.appendChild(invalidFeedbackIcon)
    html.appendChild(invalidFeedbackText)
    return html
  }

  /**
   * Container for columns
   */
  getRow () {
    const row = document.createElement('div')
    row.classList.add('jedi-row')
    return row
  }

  /**
   * A column to contain content to a specific width
   */
  getCol (xs, sm, md, lg, offsetMd) {
    const col = document.createElement('div')
    col.classList.add('jedi-col')
    col.classList.add('jedi-col-xs-' + xs)
    col.classList.add('jedi-col-sm-' + sm)
    col.classList.add('jedi-col-md-' + md)
    col.classList.add('jedi-col-lg-' + lg)

    if (offsetMd) {
      col.classList.add('jedi-col-md-offset-' + offsetMd)
    }

    return col
  }

  /**
   * Row + two columns for nav-style editors (object-nav, array-nav,
   * object-categories). Horizontal keeps the fixed full-width split (the
   * zero-width content col intentionally forces it onto a new flex line
   * below the full-width tab list) built on the regular getRow()/getCol()
   * grid. Vertical uses a plain (non-grid) flex row instead: Bootstrap's
   * `.row > *` rule forces `width: 100%` on grid columns, which would
   * fight flex-basis:auto's content-based sizing, so the shrink-to-fit nav
   * column can't share that grid. It hugs its widest pill on wider
   * containers (constrained by widthOptions.minWidth/maxWidth) and stacks
   * full-width below the container-query breakpoint in ensureNavStyles().
   */
  getNavRow (variant, widthOptions = {}) {
    if (variant === 'horizontal') {
      const row = this.getRow()
      const tabListCol = this.getCol(12, 12, 12, 12)
      const tabContentCol = this.getCol(12, 12, 0, 0)
      return { row, tabListCol, tabContentCol }
    }

    this.ensureNavStyles()

    const row = document.createElement('div')
    row.classList.add('jedi-nav-row')

    const tabListCol = document.createElement('div')
    tabListCol.classList.add('jedi-col')
    tabListCol.classList.add('jedi-nav-list-col')
    tabListCol.style.setProperty('--jedi-nav-max-width', widthOptions.maxWidth ?? '40%')
    if (widthOptions.minWidth) tabListCol.style.setProperty('--jedi-nav-min-width', widthOptions.minWidth)

    const tabContentCol = document.createElement('div')
    tabContentCol.classList.add('jedi-col')
    tabContentCol.classList.add('jedi-nav-content-col')

    return { row, tabListCol, tabContentCol }
  }

  /**
   * Mobile-first: the nav row stacks at 100% width by default; a container
   * query (not a viewport media query) switches to shrink-to-fit once the
   * row itself has >= 576px to work with, so a narrow embed (a modal, a
   * sidebar) gets the same treatment as a narrow viewport regardless of
   * how wide the page around it is. Injected once, same pattern as
   * bootstrap5.js's jedi-accordion-button-style, since inline styles can't
   * express @container.
   */
  ensureNavStyles () {
    if (document.getElementById('jedi-nav-styles')) return

    const style = document.createElement('style')
    style.id = 'jedi-nav-styles'
    style.textContent = `
      .jedi-nav-row { display: flex; flex-wrap: wrap; gap: 1rem; container-type: inline-size; }
      .jedi-nav-list-col { flex: 1 1 100%; width: 100%; max-width: 100%; }
      .jedi-nav-content-col { flex: 1 1 100%; width: 100%; min-width: 0; }
      @container (min-width: 576px) {
        .jedi-nav-list-col { flex: 0 1 auto; width: auto; max-width: var(--jedi-nav-max-width, 40%); min-width: var(--jedi-nav-min-width, auto); }
        .jedi-nav-content-col { flex: 1 1 0%; width: auto; }
      }
    `
    document.head.appendChild(style)
  }

  /**
   * Clearfix fixes layout issues in some libraries like bootstrap 3
   */
  getClearfix () {
    const clearfix = document.createElement('div')
    clearfix.classList.add('clearfix')
    return clearfix
  }

  /**
   * Tab list is a list of links that triggers tabs visibility ne at the time
   */
  getTabList () {
    const tabList = document.createElement('ul')
    tabList.classList.add('jedi-nav-list')
    return tabList
  }

  styleLegendWarning (span) {}

  /**
   * A Tab is a wrapper for content
   */
  getTab (config) {
    const list = document.createElement('li')
    const link = document.createElement('a')
    const arrayActions = document.createElement('span')
    const text = document.createElement('span')
    link.classList.add('jedi-nav-link')
    link.setAttribute('href', '#' + 'tab-pane-' + config.id)
    text.classList.add('jedi-nav-text')
    text.textContent = config.title

    if (config.hasErrors) {
      const warning = document.createElement('span')
      warning.classList.add('jedi-nav-warning')
      warning.textContent = '⚠ '

      text.insertBefore(warning, text.firstChild)

      if (config.navWarningMessage) {
        list.setAttribute('title', config.navWarningMessage)
      }
    }

    link.appendChild(arrayActions)
    link.appendChild(text)
    list.appendChild(link)
    return { list, link, arrayActions, text }
  }

  /**
   * Wrapper for tabs
   */
  getTabContent () {
    const tabContent = document.createElement('div')
    tabContent.classList.add('tab-content')
    return tabContent
  }

  /**
   * A simple table layout
   */
  getTable () {
    const container = document.createElement('div')
    const table = document.createElement('table')
    const thead = document.createElement('thead')
    const tbody = document.createElement('tbody')

    table.appendChild(thead)
    table.appendChild(tbody)
    container.appendChild(table)

    return { container, table, thead, tbody }
  }

  /**
   * Returns a <td> element
   */
  getTableDefinition (config = {}) {
    const td = document.createElement('td')

    if (config.isButtonColumn) {
      td.style.width = '1%'
    }

    td.style.whiteSpace = 'nowrap'

    return td
  }

  /**
   * Returns a <th> element
   */
  getTableHeader (config = {}) {
    const th = document.createElement('th')
    th.style.paddingLeft = '12px'
    th.style.paddingRight = '12px'
    th.style.textWrap = 'nowrap'
    th.style.verticalAlign = 'bottom'

    if (config.minWidth) {
      th.style.minWidth = config.minWidth
    }

    return th
  }

  /**
   * Set tab attributes to make it toggleable
   */
  setTabPaneAttributes (element, active, id) {
    element.setAttribute('id', 'tab-pane-' + id)
    element.classList.add('jedi-tab-pane')
  }

  /**
   * Makes an element visually hidden
   */
  visuallyHidden (element) {
    element.style.position = 'absolute'
    element.style.width = '1px'
    element.style.height = '1px'
    element.style.padding = '0'
    element.style.margin = '-1px'
    element.style.overflow = 'hidden'
    element.style.clip = 'rect(0,0,0,0)'
    element.style.border = '0'
  }

  /**
   * Reveals a visually hidden element
   */
  visuallyVisible (element) {
    element.removeAttribute('style')
  }

  /**
   * Makes an element physically hidden
   */
  physicallyHidden (element) {
    element.style.display = 'none'
  }
}

export default Theme
